import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import API from "src/API";
import { EQAuthParamType } from "src/Types";
import { EQueueConfigType } from "src/Types/ReDoc/EQueue";
import { ReturnPultStatusType, ReturnStateType } from "src/Types/ReDoc/Return";
import { Select2WebElementType } from "src/Types/ReDoc";
import { isReturnApiError, returnToastStyle } from "src/Views/EQueue/eQueueUtils";
import { PAUSE_START_KEY } from "./ReturnSlicer";
import {
	clearPauseTotalSeconds,
	commitPauseSession,
	getPauseTotalStorageKey
} from "src/Views/EQueue/pauseTimeStorage";

const showReturnError = (message: string, title: string = "re:turn") => {
	toast.error(message || "Непознанная ошибка", {
		position: "top-center",
		autoClose: 10000,
		hideProgressBar: true,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true,
		closeButton: false,
		style: returnToastStyle
	});
	console.warn(title, message);
};

type ReturnWindowsParams = {
	transfer?: boolean;
	officeId?: number;
};

type ReturnTransferParams = {
	toWindow: string;
	comment: string;
};

/** Обновление состояния виджета re:turn из ответа backend.
 *  Если сервер вернул message (например, из ReturnExceptionFilter) — показываем тост с ошибкой. */
const dispatchReturnState = (thunkAPI: { dispatch: (arg: unknown) => unknown }, state: ReturnStateType) => {
	thunkAPI.dispatch({ type: "eQueueReturn/updateState", payload: state });
	if (state.message) {
		showReturnError(state.message);
	}
};

/** Обновление счетчиков/очереди после успешного действия */
const refreshReturnQueue = (thunkAPI: { dispatch: (arg: unknown) => unknown }, withQueueList: boolean = false) => {
	thunkAPI.dispatch(returnGetQueueState());
	if (withQueueList) {
		thunkAPI.dispatch(returnGetQueue());
	}
};

const resolvePauseTotalStorageKey = (thunkAPI: { getState: () => unknown }): string | undefined => {
	const state = thunkAPI.getState() as { eQueueReturn?: { config?: EQueueConfigType } };
	const config = state.eQueueReturn?.config;
	return config ? getPauseTotalStorageKey(config) : undefined;
};

/** получить настройки электронной очереди */
export const returnGetConfig = createAsyncThunk("eQueueReturn/GetConfig", async (_, thunkAPI) => {
	const result = await API.ReturnEQ.getConfig();
	if (!result.hasError) {
		thunkAPI.dispatch({ type: "eQueueReturn/setConfig", payload: result.result });
	}
	return result;
});

/** сохранить настройки электронной очереди */
export const returnSaveConfig = createAsyncThunk("eQueueReturn/SaveConfig", async (payload: EQAuthParamType) => {
	return await API.ReturnEQ.setConfig(payload);
});

/** запустить электронную очередь */
export const returnStart = createAsyncThunk("eQueueReturn/Start", async (payload: string, thunkAPI) => {
	const data = await API.ReturnEQ.start(payload);
	if (!data.hasError) {
		const pauseTotalKey = resolvePauseTotalStorageKey(thunkAPI);
		commitPauseSession(pauseTotalKey);
		clearPauseTotalSeconds(pauseTotalKey);
		localStorage.removeItem(PAUSE_START_KEY);
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
		refreshReturnQueue(thunkAPI);
	}
	return data;
});

/** получить текущий статус ЭО */
export const returnGetStatus = createAsyncThunk("eQueueReturn/GetStatus", async (_, thunkAPI) => {
	const data = await API.ReturnEQ.getStatus();
	if (!data.hasError) {
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
		refreshReturnQueue(thunkAPI);
	}
	return data;
});

/** получить счетчики очереди */
export const returnGetQueueState = createAsyncThunk(
	"eQueueReturn/GetQueueState",
	async () => await API.ReturnEQ.getQueueState()
);

/** получить список талонов очереди */
export const returnGetQueue = createAsyncThunk(
	"eQueueReturn/GetQueue",
	async (term?: string) => await API.ReturnEQ.getQueue(term || "")
);

/** Вызов следующего посетителя */
export const returnCall = createAsyncThunk("eQueueReturn/Call", async (_, thunkAPI) => {
	const data = await API.ReturnEQ.call();
	if (!data.hasError) {
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
		refreshReturnQueue(thunkAPI, true);
	}
	return data;
});

/** Предварительный выбор следующего посетителя без фактического вызова */
export const returnPreviewNext = createAsyncThunk("eQueueReturn/PreviewNext", async (_, thunkAPI) => {
	const data = await API.ReturnEQ.previewNext();
	if (isReturnApiError(data) || !data.result) {
		showReturnError(data.errorMessage || "Не удалось выбрать следующего посетителя", "PreviewNext");
		return thunkAPI.rejectWithValue(data);
	}
	dispatchReturnState(thunkAPI, data.result as ReturnStateType);
	thunkAPI.dispatch({ type: "eQueueReturn/setViewMode", payload: "confirming" });
	refreshReturnQueue(thunkAPI, true);
	return data;
});

/** Подтверждение вызова предварительно выбранного посетителя */
export const returnConfirmCall = createAsyncThunk("eQueueReturn/ConfirmCall", async (_, thunkAPI) => {
	const data = await API.ReturnEQ.confirmCall();
	if (isReturnApiError(data) || !data.result) {
		showReturnError(data.errorMessage || "Не удалось подтвердить вызов посетителя", "ConfirmCall");
		return thunkAPI.rejectWithValue(data);
	}
	dispatchReturnState(thunkAPI, data.result as ReturnStateType);
	thunkAPI.dispatch({ type: "eQueueReturn/setViewMode", payload: "called" });
	refreshReturnQueue(thunkAPI, true);
	return data;
});

/** Получение окон для перенаправления */
export const returnGetWindows = createAsyncThunk(
	"eQueueReturn/GetWindows",
	async (params?: ReturnWindowsParams): Promise<Select2WebElementType[]> => {
		return await API.ReturnEQ.getWindows(params?.transfer ?? true, params?.officeId);
	}
);

/** Вызов посетителя по идентификатору талона */
export const returnCallById = createAsyncThunk("eQueueReturn/CallById", async (payload: string, thunkAPI) => {
	const data = await API.ReturnEQ.callById(payload);
	if (!data.hasError) {
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
		refreshReturnQueue(thunkAPI, true);
	}
	return data;
});

/** Начало обслуживания текущего посетителя */
export const returnBeginServing = createAsyncThunk("eQueueReturn/BeginServing", async (ticketId: string | undefined, thunkAPI) => {
	const data = await API.ReturnEQ.beginServing(ticketId);
	if (!data.hasError) {
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
		refreshReturnQueue(thunkAPI);
	}
	return data;
});

/** Завершение обслуживания текущего посетителя */
export const returnComplete = createAsyncThunk("eQueueReturn/Complete", async (_, thunkAPI) => {
	const data = await API.ReturnEQ.complete();
	if (!data.hasError) {
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
		refreshReturnQueue(thunkAPI, true);
	}
	return data;
});

/** Перенаправление посетителя в другое окно */
export const returnTransfer = createAsyncThunk("eQueueReturn/Transfer", async (payload: ReturnTransferParams, thunkAPI) => {
	const data = await API.ReturnEQ.transfer(payload.toWindow, payload.comment);
	if (!data.hasError) {
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
		refreshReturnQueue(thunkAPI, true);
	}
	return data;
});

/** Повторный вызов посетителя (recall_ticket → табло) */
export const returnRecall = createAsyncThunk("eQueueReturn/Recall", async (_, thunkAPI) => {
	const data = await API.ReturnEQ.recall();
	if (isReturnApiError(data) || !data.result) {
		showReturnError(data.errorMessage || "Не удалось выполнить повторный вызов", "Recall");
		return thunkAPI.rejectWithValue(data);
	}
	const state = data.result as ReturnStateType;
	const ticketNumber = state.ticket?.number;
	const message = ticketNumber
		? `Посетитель с талоном ${ticketNumber} вызван повторно`
		: "Посетитель вызван повторно";
	dispatchReturnState(thunkAPI, state);
	thunkAPI.dispatch({ type: "eQueueReturn/setViewMode", payload: "called" });
	refreshReturnQueue(thunkAPI);
	toast.success(message, {
		position: "top-center",
		autoClose: 10000,
		hideProgressBar: true,
		closeOnClick: true,
		pauseOnHover: true,
		draggable: true,
		style: returnToastStyle
	});
	return data;
});

export const returnCancelCall = createAsyncThunk("eQueueReturn/CancelCall", async (reason: string, thunkAPI) => {
	const data = await API.ReturnEQ.cancelCall(reason);
	if (!data.hasError) {
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
		refreshReturnQueue(thunkAPI, true);
	}
	return data;
});

/** Неявка посетителя */
export const returnReject = createAsyncThunk("eQueueReturn/Reject", async (_, thunkAPI) => {
	const data = await API.ReturnEQ.reject();
	if (!data.hasError) {
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
		refreshReturnQueue(thunkAPI);
	}
	return data;
});

/** Уйти на перерыв (Waiting / Selected → OnPause) */
export const returnBeginPause = createAsyncThunk("eQueueReturn/BeginPause", async (_, thunkAPI) => {
	const data = await API.ReturnEQ.beginBreak();
	if (data.hasError) {
		showReturnError(data.errorMessage || "Не удалось уйти на перерыв");
		return thunkAPI.rejectWithValue(data);
	}
	if (data.result) {
		localStorage.setItem(PAUSE_START_KEY, new Date().toISOString());
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
	}
	return data;
});

/** Атомарный переход Calling → OnPause: отменяет вызов, затем ставит перерыв */
export const returnBeginBreakFromCalling = createAsyncThunk("eQueueReturn/BeginBreakFromCalling", async (reason: string, thunkAPI) => {
	const data = await API.ReturnEQ.beginBreakFromCalling(reason);
	if (isReturnApiError(data) || !data.result) {
		showReturnError(data.errorMessage || "Не удалось перейти на перерыв", "BeginBreakFromCalling");
		return thunkAPI.rejectWithValue(data);
	}
	localStorage.setItem(PAUSE_START_KEY, new Date().toISOString());
	dispatchReturnState(thunkAPI, data.result as ReturnStateType);
	return data;
});

/** Замена предварительно выбранного талона (Selected → Calling) */
export const returnReplaceSelected = createAsyncThunk("eQueueReturn/ReplaceSelected", async (ticketId: string, thunkAPI) => {
	const data = await API.ReturnEQ.replaceSelected(ticketId);
	if (isReturnApiError(data) || !data.result) {
		showReturnError(data.errorMessage || "Не удалось заменить выбранного посетителя", "ReplaceSelected");
		return thunkAPI.rejectWithValue(data);
	}
	dispatchReturnState(thunkAPI, data.result as ReturnStateType);
	refreshReturnQueue(thunkAPI, true);
	return data;
});

/** Завершить перерыв */
export const returnEndPause = createAsyncThunk("eQueueReturn/EndPause", async (_, thunkAPI) => {
	const data = await API.ReturnEQ.endBreak();
	if (!data.hasError) {
		commitPauseSession(resolvePauseTotalStorageKey(thunkAPI));
		localStorage.removeItem(PAUSE_START_KEY);
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
		refreshReturnQueue(thunkAPI);
	}
	return data;
});

/** Завершение работы с сервером ЭО через безопасный endpoint CloseWorkday */
export const returnExit = createAsyncThunk("eQueueReturn/OnExit", async (_payload: string, thunkAPI) => {
	const data = await API.ReturnEQ.closeWorkday("manual");
	if (!data.hasError) {
		const result = data.result as ReturnStateType | undefined;
		if (result?.pultStatus === ReturnPultStatusType.Offline && !result?.closingError) {
			// Смена подтверждённо закрыта — сбрасываем UI и таймеры
			const pauseTotalKey = resolvePauseTotalStorageKey(thunkAPI);
			commitPauseSession(pauseTotalKey);
			clearPauseTotalSeconds(pauseTotalKey);
			localStorage.removeItem(PAUSE_START_KEY);
			thunkAPI.dispatch({ type: "eQueueReturn/exit", payload: result });
		} else if (result) {
			// Закрытие заблокировано — актуализируем UI из ответа сервера и показываем причину
			dispatchReturnState(thunkAPI, result);
			const errorMsg = result.closingError || result.message || "Не удалось завершить рабочий день";
			showReturnError(errorMsg);
		} else {
			// Сервер вернул пустой результат без ошибки — сбрасываем UI чтобы избежать зависания
			const pauseTotalKey = resolvePauseTotalStorageKey(thunkAPI);
			commitPauseSession(pauseTotalKey);
			clearPauseTotalSeconds(pauseTotalKey);
			localStorage.removeItem(PAUSE_START_KEY);
			thunkAPI.dispatch({ type: "eQueueReturn/exit", payload: {} });
		}
	}
	return data;
});
